// @ts-nocheck

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import some from 'lodash/some';
import forEach from 'lodash/forEach';

import './ContactSuccess.scss';
import { DarkText, Header, SendIcon } from './ContactSuccess.styles';
import { useModalOpener } from 'app/shared/contexts/ModalContext';
import Button from 'app/shared/core/Button';
import Center from 'app/shared/modules/Center';
import constants from 'app/shared/constants/ConstantsBundle';
import Container from 'app/shared/core/Container';
import ContactSimilarListingsV2 from 'app/shared/modules/hdp/form/Contact/ContactSimilarListingsV2';
import IconSend from 'images/icons/send.svg';
import IconCheckmark from 'images/icons/checkmark.svg';
import Linker from 'app/shared/modules/Linker';
import NotificationActions from 'app/shared/flux/actions/NotificationActions';
import pathsManifest from 'app/shared/utils/pathsManifest';
import Row from 'app/shared/core/Row';
import Text from 'app/shared/core/Text';
import Title from 'app/shared/modules/Title';
import userUtils from 'app/shared/utils/userUtils';

const clientToApiDevices = [constants.NOTIFICATION_DEVICE_EMAIL, constants.NOTIFICATION_DEVICE_MOBILE];

class ContactSuccess extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      optInRecSearch: false,
    };
  }

  componentDidMount() {
    const { dispatch } = this.props;
    dispatch(NotificationActions.notifications.getSettings());
  }

  handleOptInRecSearch = () => {
    if (this.state.optInRecSearch) {
      return null;
    }

    const { dispatch, subscriptions, userInfo } = this.props;
    const notificationOptions = [];
    const hasActiveSubscriptions = userUtils.hasActiveSubscriptions(subscriptions);
    const promises = [];

    if (!hasActiveSubscriptions) {
      const updateOptions = {
        stopListings: false,
        stopOther: false,
      };
      promises.push(dispatch(NotificationActions.email.updateUserSubscriptions(updateOptions, userInfo.email)));
    }

    forEach(clientToApiDevices, (deviceConstant) => {
      notificationOptions.push({
        deviceGroup: deviceConstant,
        optedOut: false,
        typeGroup: constants.NOTIFICATION_REC_SEARCH,
      });
    });
    promises.push(dispatch(NotificationActions.notifications.updateSettings(notificationOptions)));
    Promise.all(promises)
      .then(() => {
        this.setState({
          optInRecSearch: true,
        });
        return;
      })
      .catch(() => {
        // Silently handle errors
        return;
      });
  };

  handleSignIn = () => {
    const { openPromptLoginModal } = this.props;
    // Open login modal without closing ContactSuccess modal
    // Pass shouldRedirect: false to prevent homepage redirect after login
    // Pass handleLoginSuccess to close the login modal after successful login
    openPromptLoginModal({
      props: {
        shouldRedirect: false,
      },
      onClose: () => {
        // Modal closes automatically when login succeeds
        // ContactSuccess modal remains open underneath and will re-render
        // without the "Sign in" button since user is now logged in
      },
    });
  };

  render() {
    const { isPopup, recSearchPreferences, userExists, isLoggedIn, onHidePopup, onClose, listing, similarListings } =
      this.props;
    const hasSimilarListings = this.props.similarListings.length > 0;

    // Support both new modal manager (onClose) and legacy (onHidePopup)
    const handleClose = onClose || onHidePopup || (() => {});

    // Only show "Sign in" button if user exists but is NOT currently logged in
    const shouldShowSignIn = userExists && !isLoggedIn;

    return (
      <div className="ContactSuccessModal">
        <Header>
          <SendIcon src={IconSend} alt="" />
          <Title id="dialog-title" size="lg">
            Message sent!
          </Title>
        </Header>
        {!some(recSearchPreferences) && (
          <div>
            <Row>Should we email you similar listings?</Row>
            {this.state.optInRecSearch ? (
              <Button btnType="primary-inactive" size="lg">
                <img src={IconCheckmark} className="ContactSuccess-check" />
                <Text className="ContactSuccess-text">You are now subscribed.</Text>
              </Button>
            ) : (
              <Button onClick={this.handleOptInRecSearch} btnType="primary-outline" size="lg">
                <b>Yes, subscribe me!</b>
              </Button>
            )}

            <Row>
              <DarkText>
                <Text size="sm">
                  You may{' '}
                  <Linker className="ContactSuccess-link" to={pathsManifest.UserSettingsPage}>
                    adjust your email preferences
                  </Linker>{' '}
                  at any time.
                </Text>
              </DarkText>
            </Row>
          </div>
        )}
        {shouldShowSignIn && (
          <Row>
            <Container>
              <Row>Sign in to add favorites, view contacted properties, and more!</Row>
              <Button onClick={this.handleSignIn} btnType="primary-outline" size="lg">
                <b>Sign in</b>
              </Button>
            </Container>
          </Row>
        )}
        {hasSimilarListings && <ContactSimilarListingsV2 listing={listing} similarListings={similarListings} />}
        {!hasSimilarListings && isPopup && (
          <Container>
            <Row>
              <Center>
                <Button full size="lg" btnType="primary" onClick={handleClose}>
                  Awesome!
                </Button>
              </Center>
            </Row>
          </Container>
        )}
      </div>
    );
  }
}

ContactSuccess.propTypes = {
  dispatch: PropTypes.func.isRequired,
  listing: PropTypes.object,
  similarListings: PropTypes.array,
  isPopup: PropTypes.bool,
  onHidePopup: PropTypes.func,
  onClose: PropTypes.func,
  userExists: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  recSearchPreferences: PropTypes.array,
  subscriptions: PropTypes.object,
  userInfo: PropTypes.object,
  openPromptLoginModal: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => ({
  // Listing is passed directly from modal via useContactSubmission hook instead of Redux state
  // This avoids race conditions w/ SRP Contact when redux currentListing gets cleared after contact is successfully submitted
  listing: ownProps.listing,
  userInfo: state.user.info,
  recSearchPreferences: state.user.recSearchPreferences,
  subscriptions: state.user.subscriptions,
  // Check if user is currently logged in from Redux state
  // If logged in, don't show "Sign in" button regardless of userExists prop
  isLoggedIn: state.user.loggedIn,
  // Use userExists from ownProps for initial render, but isLoggedIn takes precedence
  userExists: ownProps.userExists,
});

// Wrapper component to provide modal context to the class component
const ContactSuccessWrapper = (props) => {
  const { openPromptLoginModal } = useModalOpener();
  return <ContactSuccess {...props} openPromptLoginModal={openPromptLoginModal} />;
};

export default connect(mapStateToProps)(ContactSuccessWrapper);
